/**
 * Package-scoped department and staff-role lists.
 *
 * Care-scope catalog (seed specialties):
 * - OPD only: Homeopathy, Dermatologist, Endocrinologist, Dental
 * - OPD + IPD: General Physician/Medicine, Gynaecology, Cardiology, Neurology,
 *   Psychiatry, Pulmonology, Gastroenterology, and surgical specialties
 *   except Trauma (General / Cardiothoracic / Neuro / Orthopedic / Plastic Surgeon)
 * - IPD only: Trauma Surgeon (+ facility units: OT, ICU, Ward, etc.)
 *
 * Basic / Intermediate → OPD + OPD/IPD departments (and OPD staff roles)
 * Premium → all departments and roles
 */

import { isPremiumPackageName } from "./attendanceFlow";
import { isPathModuleStaffRole } from "./pathRoleUtils";

/** Staff / desk roles used in OPD workflows (Basic + Intermediate). */
export const OPD_STAFF_ROLE_KEYS = [
  "staff",
  "frontdesk",
  "pathdesk",
  "path admin",
  "pathadmin",
  "pathology admin",
  "admin",
  "super_admin",
  "superadmin",
  "pharmacy admin",
];

/** OT / IPD-related staff roles (Premium only). */
export const IPD_STAFF_ROLE_KEYS = [
  "anesthesiologist",
  "ot_nurse",
  "circulating_ot_nurse",
  "ot_technician",
  "ot_attendant",
  "nurse",
  "ward admin",
  "wardadmin",
  "surgeon",
];

/** Mostly OPD departments (OPD ✅, IPD ❌ / rare / limited). */
const OPD_ONLY_DEPARTMENT_NAMES = [
  "homeopathy",
  "dermatologist",
  "dermatology",
  "endocrinologist",
  "endocrinology",
  "dental",
  "dentistry",
  "dentist",
];

/** OPD + IPD departments (consultation in OPD; admissions/surgeries in IPD). */
const BOTH_DEPARTMENT_NAMES = [
  "general physician",
  "gynaecology",
  "gynecology",
  "gynaecologist",
  "gynecologist",
  "obstetrics & gynaecology",
  "obstetrics and gynaecology",
  "obstetrics & gynecology",
  "obstetrics and gynecology",
  "general medicine",
  "cardiologist",
  "cardiology",
  "neurologist",
  "neurology",
  "psychiatrist",
  "psychiatry",
  "pulmonologist",
  "pulmonology",
  "gastroenterologist",
  "gastroenterology",
  "general surgeon",
  "general surgery",
  "cardiothoracic surgeon",
  "cardiothoracic surgery",
  "neurosurgeon",
  "neurosurgery",
  "orthopedic surgeon",
  "orthopaedic surgeon",
  "orthopedic surgery",
  "orthopaedic surgery",
  "orthopedics",
  "orthopaedics",
  "plastic surgeon",
  "plastic surgery",
];

/**
 * Primarily IPD / Emergency — Premium only on Basic/Intermediate packages.
 * (Trauma has no routine OPD clinic in the care-scope catalog.)
 */
const IPD_ONLY_DEPARTMENT_NAMES = [
  "trauma surgeon",
  "trauma surgery",
  "trauma",
];

/** Facility / ward units that are IPD-scoped even without a specialty name match. */
const IPD_FACILITY_WORD_KEYWORDS = [
  "ipd",
  "icu",
  "nicu",
  "picu",
  "ccu",
  "hdu",
  "ward",
];

const IPD_FACILITY_PHRASE_KEYWORDS = [
  "inpatient",
  "in-patient",
  "in patient",
  "operation theatre",
  "operation theater",
  "operating theatre",
  "operating theater",
  "operation room",
  "casualty ward",
  "emergency trauma",
];

const normalizeKey = (value) =>
  String(value || "")
    .toLowerCase()
    .trim()
    .replace(/[\s-]+/g, "_");

const normalizeLoose = (value) =>
  String(value || "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, " ");

const roleKeySet = (keys) => new Set(keys.map(normalizeKey));

const OPD_ROLE_SET = roleKeySet(OPD_STAFF_ROLE_KEYS);
const IPD_ROLE_SET = roleKeySet(IPD_STAFF_ROLE_KEYS);

const nameMatchesCatalog = (haystack, catalog) =>
  catalog.some((name) => haystack === name || haystack.includes(name));

/** True when package name is Premium. */
export const packageAllowsIpdScopes = (packageNameOrFlag) => {
  if (typeof packageNameOrFlag === "boolean") return packageNameOrFlag;
  return isPremiumPackageName(packageNameOrFlag);
};

/**
 * Resolve care scope for a department: "OPD" | "BOTH" | "IPD".
 * Prefers explicit API type, then known specialty catalogs, then facility keywords.
 */
export const resolveDepartmentCareScope = (department) => {
  if (!department) return "OPD";

  const explicit =
    department.isIpd ??
    department.isIPD ??
    department.ipd ??
    department.forIpd ??
    department.forIPD;
  if (explicit === true || explicit === 1 || explicit === "1" || explicit === "true") {
    return "IPD";
  }
  if (explicit === false || explicit === 0 || explicit === "0" || explicit === "false") {
    return "OPD";
  }

  const typeValue = normalizeLoose(
    department.type ||
      department.departmentType ||
      department.department_type ||
      department.category ||
      department.departmentCategory ||
      department.scope ||
      ""
  );
  if (typeValue) {
    if (
      typeValue === "both" ||
      typeValue === "opd+ipd" ||
      typeValue === "opd_ipd" ||
      typeValue === "opd / ipd"
    ) {
      return "BOTH";
    }
    if (/\bipd\b|in[\s_-]?patient/.test(typeValue)) return "IPD";
    if (/\bopd\b|out[\s_-]?patient/.test(typeValue)) return "OPD";
  }

  const haystack = normalizeLoose(
    `${department.name || ""} ${department.description || ""} ${department.code || ""}`
  );

  if (nameMatchesCatalog(haystack, IPD_ONLY_DEPARTMENT_NAMES)) return "IPD";
  if (nameMatchesCatalog(haystack, OPD_ONLY_DEPARTMENT_NAMES)) return "OPD";
  if (nameMatchesCatalog(haystack, BOTH_DEPARTMENT_NAMES)) return "BOTH";

  if (
    IPD_FACILITY_WORD_KEYWORDS.some((keyword) =>
      new RegExp(`\\b${keyword}\\b`, "i").test(haystack)
    )
  ) {
    return "IPD";
  }
  if (IPD_FACILITY_PHRASE_KEYWORDS.some((keyword) => haystack.includes(keyword))) {
    return "IPD";
  }
  // Standalone "OT" / theatre rooms (not "otolaryngology")
  if (/\bot\b/.test(haystack) || /\btheatr[e|er]\b/.test(haystack)) {
    return "IPD";
  }

  // Unknown specialties default to OPD-compatible so Basic/Intermediate still work
  return "OPD";
};

/**
 * True for primarily IPD / Premium-only departments (not shown on Basic/Intermediate).
 * OPD-only and OPD+IPD specialties return false so they remain selectable.
 */
export const isIpdDepartment = (department) =>
  resolveDepartmentCareScope(department) === "IPD";

/** True when department supports OPD (OPD-only or OPD+IPD). */
export const isOpdDepartment = (department) => {
  const scope = resolveDepartmentCareScope(department);
  return scope === "OPD" || scope === "BOTH";
};

/** True when department supports IPD admissions (IPD-only or OPD+IPD). */
export const isIpdCompatibleDepartment = (department) => {
  const scope = resolveDepartmentCareScope(department);
  return scope === "IPD" || scope === "BOTH";
};

export const isIpdStaffRoleName = (roleName) => {
  const key = normalizeKey(roleName);
  if (IPD_ROLE_SET.has(key)) return true;
  // OT_* pattern for future IPD roles from API
  return key.startsWith("ot_") || key.includes("ward");
};

export const isOpdStaffRoleName = (roleName) => {
  const key = normalizeKey(roleName);
  if (isIpdStaffRoleName(roleName)) return false;
  if (OPD_ROLE_SET.has(key)) return true;
  if (isPathModuleStaffRole(roleName)) return true;
  // Unknown non-IPD roles treated as OPD-compatible so new OPD roles still appear
  return !isIpdStaffRoleName(roleName);
};

/**
 * Filter departments for dropdowns / dashboards.
 * Basic/Intermediate → OPD + OPD/IPD; Premium → all.
 * Keep currently selected ids so edit forms don't break on Premium→lower package edge cases.
 */
export const filterDepartmentsForPackage = (
  departments,
  { isPremium = false, keepIds = [] } = {}
) => {
  const list = Array.isArray(departments) ? departments : [];
  if (packageAllowsIpdScopes(isPremium)) return list;

  const keep = new Set(
    (Array.isArray(keepIds) ? keepIds : [keepIds])
      .filter((id) => id != null && id !== "")
      .map((id) => Number(id))
  );

  return list.filter((dept) => {
    if (keep.has(Number(dept?.id))) return true;
    return isOpdDepartment(dept);
  });
};

const keepIdSet = (keepIds) =>
  new Set(
    (Array.isArray(keepIds) ? keepIds : [keepIds])
      .filter((id) => id != null && id !== "")
      .map((id) => Number(id))
  );

/** Departments selectable for OPD registration / outpatient appointments. */
export const filterDepartmentsForOpdWorkflow = (
  departments,
  { keepIds = [] } = {}
) => {
  const list = Array.isArray(departments) ? departments : [];
  const keep = keepIdSet(keepIds);
  return list.filter((dept) => {
    if (keep.has(Number(dept?.id))) return true;
    return isOpdDepartment(dept);
  });
};

/** Departments selectable for IPD admissions (excludes OPD-only specialties). */
export const filterDepartmentsForIpdWorkflow = (
  departments,
  { keepIds = [] } = {}
) => {
  const list = Array.isArray(departments) ? departments : [];
  const keep = keepIdSet(keepIds);
  return list.filter((dept) => {
    if (keep.has(Number(dept?.id))) return true;
    return isIpdCompatibleDepartment(dept);
  });
};

/**
 * Department type options for create/edit.
 * Basic/Intermediate cannot create IPD-only departments.
 */
export const filterDepartmentTypeOptionsForPackage = (
  options,
  { isPremium = false, keepValue = null } = {}
) => {
  const list = Array.isArray(options) ? options : [];
  if (packageAllowsIpdScopes(isPremium)) return list;
  return list.filter((opt) => {
    const value = String(opt?.value ?? opt ?? "").toUpperCase();
    if (keepValue != null && value === String(keepValue).toUpperCase()) return true;
    return value === "OPD" || value === "BOTH";
  });
};

/**
 * Filter staff user-role options for create/edit.
 * @param hasPathPackageAccess when false, pathdesk / pathology admin are hidden (Basic).
 */
export const filterStaffRolesForPackage = (
  roles,
  {
    isPremium = false,
    hasPathPackageAccess = true,
    keepRoleNames = [],
    excludeRoleNames = ["doctor", "surgeon"],
  } = {}
) => {
  const list = Array.isArray(roles) ? roles : [];
  const keep = new Set(
    (Array.isArray(keepRoleNames) ? keepRoleNames : [keepRoleNames])
      .filter(Boolean)
      .map((name) => normalizeKey(name))
  );
  const exclude = new Set(
    (Array.isArray(excludeRoleNames) ? excludeRoleNames : [excludeRoleNames]).map(
      (name) => normalizeKey(name)
    )
  );
  const allowIpd = packageAllowsIpdScopes(isPremium);

  return list.filter((role) => {
    const roleName = role?.roleName || role?.username || role;
    const key = normalizeKey(roleName);
    if (!key) return false;
    if (exclude.has(key) && !keep.has(key)) return false;
    if (keep.has(key)) return true;
    if (isPathModuleStaffRole(roleName) && !hasPathPackageAccess) return false;
    if (isIpdStaffRoleName(roleName)) return allowIpd;
    return isOpdStaffRoleName(roleName);
  });
};

/** Doctor user-role options: surgeon is IPD/surgical (Premium only). */
export const filterDoctorRolesForPackage = (
  roles = ["doctor", "surgeon"],
  { isPremium = false, keepRole = null } = {}
) => {
  const list = Array.isArray(roles) ? roles : [];
  if (packageAllowsIpdScopes(isPremium)) return list;
  return list.filter((role) => {
    const key = normalizeKey(role);
    if (keepRole && key === normalizeKey(keepRole)) return true;
    return key === "doctor";
  });
};
